import React, { useState } from 'react';
import './AIChatFeedback.css';

/**
 * AI Chat feedback: accordion with copy reference text and Intercom prefilled.
 * Format: AI chat, Conversation ID, Exchange ID, User ID (if available). No widget title/ID.
 */
export function AIChatFeedback({ conversationId, exchangeId, userId }) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [copied, setCopied] = useState(false);

    if (!conversationId && !exchangeId) {
        return null;
    }

    const buildIdLines = () => {
        const lines = ['AI chat'];
        if (conversationId) lines.push(`Conversation ID: ${conversationId}`);
        if (exchangeId) lines.push(`Exchange ID: ${exchangeId}`);
        if (userId) lines.push(`User ID: ${userId}`);
        return lines;
    };

    const handleCopyIds = async (e) => {
        e.stopPropagation();
        const idInfo = buildIdLines();
        const textToCopy = `AI Summary Feedback Reference\n${'─'.repeat(30)}\n${idInfo.join('\n')}\n${'─'.repeat(30)}\n\nHave feedback on this summary? Please describe below:\n`;

        try {
            await navigator.clipboard.writeText(textToCopy);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy IDs:', err);
            const textarea = document.createElement('textarea');
            textarea.value = textToCopy;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const handleProvideFeedback = (e) => {
        e.stopPropagation();

        if (typeof window.Intercom === 'function') {
            window.Intercom('update', {
                customAttributes: {
                    feedback_conversation_id: conversationId || '',
                    feedback_exchange_id: exchangeId || '',
                    feedback_user_id: userId || '',
                    feedback_trigger: 'ai_chat_feedback',
                },
            });

            const referenceLines = [];
            referenceLines.push('─'.repeat(24));
            referenceLines.push('REFERENCE INFORMATION (DO NOT EDIT)');
            referenceLines.push('─'.repeat(24));
            referenceLines.push('AI chat');
            if (conversationId) referenceLines.push(`Conversation ID: ${conversationId}`);
            if (exchangeId) referenceLines.push(`Exchange ID: ${exchangeId}`);
            if (userId) referenceLines.push(`User ID: ${userId}`);
            referenceLines.push('─'.repeat(24));

            const message = `AI Summary Feedback\n\n${referenceLines.join('\n')}\n\nPlease describe your feedback below:\n`;
            window.Intercom('showNewMessage', message);
        } else {
            console.warn('Intercom not available. Feedback details:', {
                conversationId,
                exchangeId,
                userId,
            });
            alert(
                'Support chat is currently unavailable. Please use "Copy Details" to include reference information in an email to support.'
            );
        }
    };

    const handleToggle = () => {
        setIsExpanded(!isExpanded);
    };

    return (
        <div className={`ai-chat-feedback ${isExpanded ? 'ai-chat-feedback-expanded' : 'ai-chat-feedback-collapsed'}`}>
            <div className="ai-chat-feedback-preview" onClick={handleToggle}>
                <span className="ai-chat-feedback-preview-text">Have feedback on this chat response?</span>
                <svg
                    className={`ai-chat-feedback-chevron ${isExpanded ? 'ai-chat-feedback-chevron-expanded' : ''}`}
                    width="10"
                    height="10"
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M3 4.5L6 7.5L9 4.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </div>
            {isExpanded && (
                <div className="ai-chat-feedback-content">
                    <div className="ai-chat-feedback-explainer">
                        Have feedback on this chat response? If something doesn't look or work right, use the buttons below
                        to share your thoughts or copy the reference IDs to include in a support request.
                    </div>
                    <div className="ai-chat-feedback-row">
                        <div className="ai-chat-feedback-ids">
                            {conversationId && (
                                <span className="ai-chat-feedback-id">
                                    <span className="ai-chat-feedback-id-label">Conv:</span>
                                    <span className="ai-chat-feedback-id-value">{conversationId}</span>
                                </span>
                            )}
                            {exchangeId && (
                                <span className="ai-chat-feedback-id">
                                    <span className="ai-chat-feedback-id-label">Exchange:</span>
                                    <span className="ai-chat-feedback-id-value">{exchangeId}</span>
                                </span>
                            )}
                            {userId && (
                                <span className="ai-chat-feedback-id">
                                    <span className="ai-chat-feedback-id-label">User:</span>
                                    <span className="ai-chat-feedback-id-value">{userId}</span>
                                </span>
                            )}
                        </div>
                        <div className="ai-chat-feedback-actions">
                            <button
                                className="ai-chat-feedback-btn ai-chat-feedback-btn-primary"
                                onClick={handleProvideFeedback}
                                title="Provide feedback on this AI summary"
                            >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM20 16H5.17L4 17.17V4H20V16Z"
                                        fill="currentColor"
                                    />
                                    <path
                                        d="M12 15L13.57 11.57L17 10L13.57 8.43L12 5L10.43 8.43L7 10L10.43 11.57L12 15Z"
                                        fill="currentColor"
                                    />
                                </svg>
                                Share Feedback
                            </button>
                            <button
                                className={`ai-chat-feedback-btn ai-chat-feedback-btn-secondary ${copied ? 'ai-chat-feedback-btn-copied' : ''}`}
                                onClick={handleCopyIds}
                                title="Copy reference IDs for email"
                            >
                                {copied ? (
                                    <>
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                            <path
                                                d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z"
                                                fill="currentColor"
                                            />
                                        </svg>
                                        Copied!
                                    </>
                                ) : (
                                    <>
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                            <path
                                                d="M16 1H4C2.9 1 2 1.9 2 3V17H4V3H16V1ZM19 5H8C6.9 5 6 5.9 6 7V21C6 22.1 6.9 23 8 23H19C20.1 23 21 22.1 21 21V7C21 5.9 20.1 5 19 5ZM19 21H8V7H19V21Z"
                                                fill="currentColor"
                                            />
                                        </svg>
                                        Copy Details
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
